'use client';

import { startCase } from 'es-toolkit';
import { isAlive } from 'mobx-state-tree';
import { toast } from 'sonner';
import * as THREE from 'three';

import * as M from '../../properties';
import { createOnChange, getParentProperty, getPropertySources } from '../../propertyFunctions';
import { type IPropertyStore } from '../../propertyStore';
import { PropertyType } from '../../propertyTypes';

import type { ObjectProperty } from '../../properties';
import type { IProperty, SelectOptionsTypes, ValueChangeContext, ValueChangeHandler } from '../../types';
import type { Instance } from 'mobx-state-tree';


export const GEOMETRY_TYPES = {
  'Box Geometry': THREE.BoxGeometry,
  'Sphere Geometry': THREE.SphereGeometry,
  'Cylinder Geometry': THREE.CylinderGeometry,
  'Cone Geometry': THREE.ConeGeometry,
  'Plane Geometry': THREE.PlaneGeometry,
  'Circle Geometry': THREE.CircleGeometry,
  'Ring Geometry': THREE.RingGeometry,
  'Torus Geometry': THREE.TorusGeometry,
  'Torus Knot Geometry': THREE.TorusKnotGeometry,
  'Capsule Geometry': THREE.CapsuleGeometry,
  'Dodecahedron Geometry': THREE.DodecahedronGeometry,
  'Icosahedron Geometry': THREE.IcosahedronGeometry,
  'Octahedron Geometry': THREE.OctahedronGeometry,
  'Tetrahedron Geometry': THREE.TetrahedronGeometry,
} as const;

export type GeometryTypeKeys = keyof typeof GEOMETRY_TYPES;

export const getGeometryType = (geometry: any) => {
  if (!geometry) return '';
  return Object.entries(GEOMETRY_TYPES).find(([_, GeometryClass]) => geometry instanceof GeometryClass)?.[0] ?? '';
};

// Default parameters for each geometry type
const GEOMETRY_DEFAULT_PARAMS: Record<string, any[]> = {
  BoxGeometry: [1, 1, 1, 1, 1, 1], // width, height, depth, widthSegments, heightSegments, depthSegments
  SphereGeometry: [1, 32, 16, 0, Math.PI * 2, 0, Math.PI], // radius, widthSegments, heightSegments, phiStart, phiLength, thetaStart, thetaLength
  CylinderGeometry: [1, 1, 1, 32, 1, false, 0, Math.PI * 2], // radiusTop, radiusBottom, height, radialSegments, heightSegments, openEnded, thetaStart, thetaLength
  ConeGeometry: [1, 1, 32, 1, false, 0, Math.PI * 2], // radius, height, radialSegments, heightSegments, openEnded, thetaStart, thetaLength
  PlaneGeometry: [1, 1, 1, 1], // width, height, widthSegments, heightSegments
  CircleGeometry: [1, 32, 0, Math.PI * 2], // radius, segments, thetaStart, thetaLength
  RingGeometry: [0.5, 1, 32, 1, 0, Math.PI * 2], // innerRadius, outerRadius, thetaSegments, phiSegments, thetaStart, thetaLength
  TorusGeometry: [1, 0.4, 16, 32, Math.PI * 2], // radius, tube, radialSegments, tubularSegments, arc
  TorusKnotGeometry: [1, 0.4, 64, 8, 2, 3], // radius, tube, tubularSegments, radialSegments, p, q
  CapsuleGeometry: [1, 1, 4, 8], // radius, height, capSegments, radialSegments
  DodecahedronGeometry: [1, 0], // radius, detail
  IcosahedronGeometry: [1, 0], // radius, detail
  OctahedronGeometry: [1, 0], // radius, detail
  TetrahedronGeometry: [1, 0], // radius, detail
};

interface GeometryPropertyConfig {
  type: PropertyType;
  min?: number;
  max?: number;
  step?: number;
  options?: SelectOptionsTypes;
  unit?: string;
}

const GEOMETRY_PROPERTY_CONFIGS: Record<string, GeometryPropertyConfig> = {
  // Box Geometry
  width: { type: PropertyType.Number, min: 0.01, step: 0.1 },
  height: { type: PropertyType.Number, min: 0.01, step: 0.1 },
  depth: { type: PropertyType.Number, min: 0.01, step: 0.1 },
  widthSegments: { type: PropertyType.Number, min: 1, max: 1000, step: 1 },
  heightSegments: { type: PropertyType.Number, min: 1, max: 1000, step: 1 },
  depthSegments: { type: PropertyType.Number, min: 1, max: 1000, step: 1 },

  // Sphere Geometry
  radius: { type: PropertyType.Number, min: 0.01, step: 0.1 },
  phiStart: { type: PropertyType.Number, step: 0.1, unit: 'rad' },
  phiLength: { type: PropertyType.Number, step: 0.1, unit: 'rad' },
  thetaStart: { type: PropertyType.Number, step: 0.1, unit: 'rad' },
  thetaLength: { type: PropertyType.Number, step: 0.1, unit: 'rad' },

  // Cylinder/Cone Geometry
  radiusTop: { type: PropertyType.Number, min: 0, step: 0.1 },
  radiusBottom: { type: PropertyType.Number, min: 0, step: 0.1 },
  radialSegments: { type: PropertyType.Number, min: 3, max: 1000, step: 1 },
  openEnded: { type: PropertyType.Boolean },

  // Circle/Ring Geometry
  segments: { type: PropertyType.Number, min: 3, max: 1000, step: 1 },
  innerRadius: { type: PropertyType.Number, min: 0, step: 0.1 },
  outerRadius: { type: PropertyType.Number, min: 0.01, step: 0.1 },
  thetaSegments: { type: PropertyType.Number, min: 3, max: 1000, step: 1 },
  phiSegments: { type: PropertyType.Number, min: 1, max: 1000, step: 1 },

  // Torus Geometry
  tube: { type: PropertyType.Number, min: 0.01, step: 0.01 },
  tubularSegments: { type: PropertyType.Number, min: 3, max: 1000, step: 1 },
  arc: { type: PropertyType.Number, step: 0.1, unit: 'rad' },

  // Torus Knot Geometry
  p: { type: PropertyType.Number, min: 1, max: 100, step: 1 },
  q: { type: PropertyType.Number, min: 1, max: 100, step: 1 },

  // Capsule Geometry
  capSegments: { type: PropertyType.Number, min: 1, max: 100, step: 1 },

  // Polyhedron Geometry
  detail: { type: PropertyType.Number, min: 0, max: 10, step: 1 },
};

const getGeometryProperties = (geometry: THREE.BufferGeometry): string[] => {
  if (geometry instanceof THREE.BoxGeometry) {
    return ['width', 'height', 'depth', 'widthSegments', 'heightSegments', 'depthSegments'];
  } else if (geometry instanceof THREE.SphereGeometry) {
    return ['radius', 'widthSegments', 'heightSegments', 'phiStart', 'phiLength', 'thetaStart', 'thetaLength'];
  } else if (geometry instanceof THREE.CylinderGeometry) {
    return ['radiusTop', 'radiusBottom', 'height', 'radialSegments', 'heightSegments', 'openEnded', 'thetaStart', 'thetaLength'];
  } else if (geometry instanceof THREE.ConeGeometry) {
    return ['radius', 'height', 'radialSegments', 'heightSegments', 'openEnded', 'thetaStart', 'thetaLength'];
  } else if (geometry instanceof THREE.PlaneGeometry) {
    return ['width', 'height', 'widthSegments', 'heightSegments'];
  } else if (geometry instanceof THREE.CircleGeometry) {
    return ['radius', 'segments', 'thetaStart', 'thetaLength'];
  } else if (geometry instanceof THREE.RingGeometry) {
    return ['innerRadius', 'outerRadius', 'thetaSegments', 'phiSegments', 'thetaStart', 'thetaLength'];
  } else if (geometry instanceof THREE.TorusGeometry) {
    return ['radius', 'tube', 'radialSegments', 'tubularSegments', 'arc'];
  } else if (geometry instanceof THREE.TorusKnotGeometry) {
    return ['radius', 'tube', 'tubularSegments', 'radialSegments', 'p', 'q'];
  } else if (geometry instanceof THREE.CapsuleGeometry) {
    return ['radius', 'height', 'capSegments', 'radialSegments'];
  } else if (
    geometry instanceof THREE.DodecahedronGeometry ||
    geometry instanceof THREE.IcosahedronGeometry ||
    geometry instanceof THREE.OctahedronGeometry ||
    geometry instanceof THREE.TetrahedronGeometry
  ) {
    return ['radius', 'detail'];
  }

  return [];
};

/** Gets parameters from geometry to use when creating a new one */
const getGeometryParams = (geometry: THREE.BufferGeometry): any[] => {
  if (geometry instanceof THREE.BoxGeometry) {
    return [
      geometry.parameters?.width ?? 1,
      geometry.parameters?.height ?? 1,
      geometry.parameters?.depth ?? 1,
      geometry.parameters?.widthSegments ?? 1,
      geometry.parameters?.heightSegments ?? 1,
      geometry.parameters?.depthSegments ?? 1
    ];
  }

  if (geometry instanceof THREE.SphereGeometry) {
    return [
      geometry.parameters?.radius ?? 1,
      geometry.parameters?.widthSegments ?? 32,
      geometry.parameters?.heightSegments ?? 16,
      geometry.parameters?.phiStart ?? 0,
      geometry.parameters?.phiLength ?? Math.PI * 2,
      geometry.parameters?.thetaStart ?? 0,
      geometry.parameters?.thetaLength ?? Math.PI
    ];
  }

  if (geometry instanceof THREE.CylinderGeometry) {
    return [
      geometry.parameters?.radiusTop ?? 1,
      geometry.parameters?.radiusBottom ?? 1,
      geometry.parameters?.height ?? 1,
      geometry.parameters?.radialSegments ?? 32,
      geometry.parameters?.heightSegments ?? 1,
      geometry.parameters?.openEnded ?? false,
      geometry.parameters?.thetaStart ?? 0,
      geometry.parameters?.thetaLength ?? Math.PI * 2
    ];
  }

  if (geometry instanceof THREE.ConeGeometry) {
    return [
      geometry.parameters?.radius ?? 1,
      geometry.parameters?.height ?? 1,
      geometry.parameters?.radialSegments ?? 32,
      geometry.parameters?.heightSegments ?? 1,
      geometry.parameters?.openEnded ?? false,
      geometry.parameters?.thetaStart ?? 0,
      geometry.parameters?.thetaLength ?? Math.PI * 2
    ];
  }

  if (geometry instanceof THREE.PlaneGeometry) {
    return [
      geometry.parameters?.width ?? 1,
      geometry.parameters?.height ?? 1,
      geometry.parameters?.widthSegments ?? 1,
      geometry.parameters?.heightSegments ?? 1
    ];
  }

  if (geometry instanceof THREE.CircleGeometry) {
    return [
      geometry.parameters?.radius ?? 1,
      geometry.parameters?.segments ?? 32,
      geometry.parameters?.thetaStart ?? 0,
      geometry.parameters?.thetaLength ?? Math.PI * 2
    ];
  }

  if (geometry instanceof THREE.RingGeometry) {
    return [
      geometry.parameters?.innerRadius ?? 0.5,
      geometry.parameters?.outerRadius ?? 1,
      geometry.parameters?.thetaSegments ?? 32,
      geometry.parameters?.phiSegments ?? 1,
      geometry.parameters?.thetaStart ?? 0,
      geometry.parameters?.thetaLength ?? Math.PI * 2
    ];
  }

  if (geometry instanceof THREE.TorusGeometry) {
    return [
      geometry.parameters?.radius ?? 1,
      geometry.parameters?.tube ?? 0.4,
      geometry.parameters?.radialSegments ?? 16,
      geometry.parameters?.tubularSegments ?? 32,
      geometry.parameters?.arc ?? Math.PI * 2
    ];
  }

  if (geometry instanceof THREE.TorusKnotGeometry) {
    return [
      geometry.parameters?.radius ?? 1,
      geometry.parameters?.tube ?? 0.4,
      geometry.parameters?.tubularSegments ?? 64,
      geometry.parameters?.radialSegments ?? 8,
      geometry.parameters?.p ?? 2,
      geometry.parameters?.q ?? 3
    ];
  }

  if (geometry instanceof THREE.CapsuleGeometry) {
    return [
      geometry.parameters?.radius ?? 1,
      geometry.parameters?.height ?? 1,
      geometry.parameters?.capSegments ?? 4,
      geometry.parameters?.radialSegments ?? 8
    ];
  }

  if (
    geometry instanceof THREE.DodecahedronGeometry ||
    geometry instanceof THREE.IcosahedronGeometry ||
    geometry instanceof THREE.OctahedronGeometry ||
    geometry instanceof THREE.TetrahedronGeometry
  ) {
    return [
      geometry.parameters?.radius ?? 1,
      geometry.parameters?.detail ?? 0
    ];
  }

  // For other geometries, return default parameters
  const geometryType = getGeometryType(geometry);
  const className = geometryType.replace(' ', '');
  return GEOMETRY_DEFAULT_PARAMS[className] ?? [1, 1, 1];
};

/**
 * buildGeometryControls
 */
export function buildGeometryControls(store: IPropertyStore, onChange?: ValueChangeHandler): Instance<typeof ObjectProperty> | undefined {
  const objects = store.targets;
  if (objects.length === 0) return undefined;

  const geometry = objects[0];
  if (!(geometry instanceof THREE.BufferGeometry)) return undefined;

  // Create the root geometry property
  const rootProperty = M.ObjectPropertyBuilder.create({
    key: `${geometry.uuid}`,
    path: '',
    label: getGeometryType(geometry),
    type: PropertyType.Object,
    isHeader: true,
    value: geometry,
    sources: store.sources,
    store
  });
  populateGeometryControls(rootProperty, true, false, onChange);
  return rootProperty;
}

/**
 * populateGeometryControls
 */
export function populateGeometryControls(parent: Instance<typeof ObjectProperty>, reset: boolean, editInline: boolean, onChange?: ValueChangeHandler, geometryTemplate?: THREE.BufferGeometry | null) {
  if (reset) {
    parent.clearProperties();
  }

  const sources = getPropertySources(parent);
  const objects = sources?.map(source => source.target) ?? [];
  const geometry = objects[0] instanceof THREE.BufferGeometry ? objects[0] : undefined;

  // console.log('populateGeometryControls', { editInline, geometry, objects });
  if (!editInline && !parent.label) {
    parent.setLabel('Geometry');
  }

  const store = parent.store;
  if (!store) return;

  if (onChange) {
    parent.on('value', onChange);
  }
  // console.log('populateGeometryControls', getGeometryType(geometry));

  const parentPath = parent.path;
  const disabled = parent.disabled;

  // Add geometry type selector at the top
  const templateGeometryType = geometryTemplate ? getGeometryType(geometryTemplate) : undefined;
  const isGeometryTypeOverride = geometryTemplate !== null && templateGeometryType !== undefined;
  const geometrySelectProperty = M.SelectProperty.create({
    key: editInline ? `${parentPath}` : `${parentPath}.geometryType`,
    path: editInline ? `${parentPath}` : `${parentPath}.geometryType`,
    parentPath,
    label: editInline ? parent.label : 'Geometry Type',
    type: PropertyType.Select,
    value: getGeometryType(geometry),
    placeholder: 'None',
    disabled,
    defaultValue: isGeometryTypeOverride ? templateGeometryType : undefined,
    isOverride: isGeometryTypeOverride
  });
  geometrySelectProperty.setOptions(Object.keys(GEOMETRY_TYPES).map(type => ({ label: type, value: type })));
  geometrySelectProperty.setStore(store);
  geometrySelectProperty.on('value', createOnChange((target: any, path: string, value: string, context: ValueChangeContext) => {
    handleGeometryTypeChange(target, path, value, editInline, context, onChange, geometryTemplate);
  }));
  if (editInline) {
    parent.setSelectProperty(geometrySelectProperty);
    parent.setIsEditInline(true);
  } else {
    parent.setIsEditInline(false);
    parent.addProperty(geometrySelectProperty);
  }
  // if (onChange) {
  //   geometrySelectProperty.on('value', onChange);
  // }

  if (!geometry) return;

  // Add basic geometry info (read-only)
  const vertexCountProperty = M.NumberProperty.create({
    key: `${geometry.uuid}.vertexCount`,
    path: `${parentPath}.vertexCount`,
    label: 'Vertex Count',
    type: PropertyType.Number,
    value: geometry.attributes.position?.count ?? 0,
    readOnly: true,
    disabled
  });
  vertexCountProperty.setStore(store);
  parent.addProperty(vertexCountProperty);

  // Create properties for each supported geometry property
  const geometryProperties = getGeometryProperties(geometry);
  for (const propPath of geometryProperties) {
    const property = createGeometryProperty(geometry, propPath, parent.path, store, onChange, disabled, geometryTemplate);
    if (property) {
      parent.addProperty(property);
    }
  }
}

/**
 * createGeometryProperty
 */
function createGeometryProperty(
  geometry: THREE.BufferGeometry,
  path: string,
  parentPath: string,
  store: IPropertyStore,
  onChange?: ValueChangeHandler,
  disabled: boolean = false,
  geometryTemplate?: THREE.BufferGeometry | null
): IProperty | undefined {
  const value = (geometry as any).parameters?.[path];
  if (value === undefined || value === null) return undefined;

  const config = GEOMETRY_PROPERTY_CONFIGS[path];
  if (!config) return undefined;

  const key = `${geometry.uuid}.${path}`;
  const label = startCase(path.split('.').pop() ?? '');

  // Get template value for override detection
  const templateValue = geometryTemplate && (geometryTemplate as any).parameters?.[path];
  const isOverride = geometryTemplate !== null && templateValue !== undefined;

  const baseParams = { key, path, parentPath, label, disabled };

  let property: IProperty;

  switch (config.type) {
    case PropertyType.Boolean: {
      property = M.BooleanProperty.create({
        ...baseParams,
        type: PropertyType.Boolean,
        value,
        defaultValue: isOverride ? templateValue : undefined,
        isOverride
      });
      break;
    }
    case PropertyType.Number: {
      property = M.NumberProperty.create({
        ...baseParams,
        type: PropertyType.Number,
        value,
        min: config.min,
        max: config.max,
        step: config.step,
        unit: config.unit,
        defaultValue: isOverride ? templateValue : undefined,
        isOverride
      });
      break;
    }
    case PropertyType.Select: {
      const selectProperty = M.SelectProperty.create({
        ...baseParams,
        type: PropertyType.Select,
        value: value.toString(),
        defaultValue: isOverride ? templateValue?.toString() : undefined,
        isOverride
      });
      if (config.options) {
        selectProperty.setOptions(config.options);
      }
      property = selectProperty;
      break;
    }
    default:
      return undefined;
  }
  property.setStore(store);

  property.on('value', createOnChange((target: any, path: string, value: any, context: ValueChangeContext) => {
    handleGeometryPropertyChange(target, path, value, context);
  }));
  if (onChange) {
    property.on('value', onChange);
  }

  return property;
}

/** Handles changing the geometry type by creating a new geometry instance with default parameters and updating all sources */
function handleGeometryTypeChange(
  target: any,
  path: string,
  value: string,
  editInline: boolean,
  context: ValueChangeContext,
  onChange?: ValueChangeHandler,
  geometryTemplate?: THREE.BufferGeometry | null
) {
  value = typeof value === 'string' ? value : '';
  const GeometryClass = GEOMETRY_TYPES[value as GeometryTypeKeys];
  // Get default parameters for the new geometry type
  const className = value.replace(' ', '');
  const defaultParams = GEOMETRY_DEFAULT_PARAMS[className] ?? [1, 1, 1];

  // Create new geometry of the selected type
  let newGeometry = null;
  try {
    newGeometry = GeometryClass ? new GeometryClass(...defaultParams) : null;
  } catch (error) {
    console.error('Failed to create geometry:', { error, className });
    toast.error(`Failed to create geometry: ${className}`);
  }

  const parentProperty = getParentProperty(context.property);
  if (!parentProperty) return;

  // Find the parent cached object that has this geometry and replace it
  const sources = parentProperty.sources ?? [];
  sources.forEach((source, index) => {
    console.log('source', { source: source.target });
    // Clean up the old geometry
    if (source.target instanceof THREE.BufferGeometry) {
      source.target.dispose();
    }
    // Update the source target to point to the new geometry
    source.target = newGeometry ? (index === 0 ? newGeometry : newGeometry.clone()) : null;
    target = source.target;
  });
  parentProperty.setValue(newGeometry, sources);

  // Rebuild the controls with the new geometry
  setTimeout(() => {
    if (isAlive(parentProperty)) {
      populateGeometryControls(parentProperty, true, editInline, onChange, geometryTemplate);
    }
  }, 0);
}

/**
   * handleGeometryPropertyChange - Handles changing individual geometry properties (recreates the geometry)
   */
function handleGeometryPropertyChange(
  target: any,
  path: string,
  value: any,
  context: ValueChangeContext
) {
  if (target instanceof THREE.BufferGeometry) {
    // Get the property name from the path (e.g., "geometry.radius" -> "radius")
    const propertyName = path.split('.').pop();
    if (!propertyName) return;

    // Update the geometry parameter
    const parameters = (target as any).parameters;
    if (parameters) {
      parameters[propertyName] = value;

      // Get the current geometry type and recreate it with updated parameters
      const geometryType = getGeometryType(target);
      const GeometryClass = GEOMETRY_TYPES[geometryType as GeometryTypeKeys];

      if (GeometryClass) {
        // Get all current parameters and create new geometry
        const currentParams = getGeometryParams(target);
        const newGeometry = new GeometryClass(...currentParams);

        // Copy the new geometry data to the existing geometry
        target.copy(newGeometry);

        // Update vertex count in the UI
        const parentProperty = getParentProperty(context.property);
        if (parentProperty) {
          const propertiesArray = Array.from(parentProperty.properties.values());
          const vertexCountProperty = propertiesArray.find(
            (prop: any) => prop.path.endsWith('.vertexCount')
          );
          if (vertexCountProperty) {
            vertexCountProperty.setValue(target.attributes.position?.count ?? 0);
          }
        }

        // Dispose of the temporary geometry
        newGeometry.dispose();
      }
    }
  }
}